import React from 'react';
import './AiVisibilityReportList.scss';
import { useTranslation } from 'react-i18next';
import { DataTable } from '../../../shared/components/common/data-table/DataTable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { format } from 'date-fns';
import type {
  AiVisibilityOverviewReport,
  AiVisibilityOverviewRun,
} from '@brand24-original/llm-types';
import {
  MetadataBadge,
  MetadataBadgeVariant,
} from '../../../shared/components/metadata-badge/MetadataBadge';
import EmptyPanel from '../../../shared/components/empty-panel/EmptyPanel';
import {
  formatPeriodLabel,
  periodLocaleFor,
} from '../../../shared/helpers/filters/formatPeriodLabel';

const STATUS_VARIANT: Record<string, MetadataBadgeVariant> = {
  GENERATING: 'generating',
  FAILED: 'danger',
};

interface AiVisibilityReportListProps {
  activeRuns: AiVisibilityOverviewRun[];
  failedRuns: AiVisibilityOverviewRun[];
  reports: AiVisibilityOverviewReport[];
  totalReports: number;
  first: number;
  pageSize: number;
  onPage: (event: { first: number; rows: number }) => void;
  onDismissFailed: (runId: number) => void;
  onCancelRun: (runId: number) => void;
  onDeleteReport: (reportId: number) => void;
  onDownload: (reportId: number, periodFrom: string, periodTo: string) => void;
  busy?: boolean;
}

const AiVisibilityReportList: React.FC<AiVisibilityReportListProps> = ({
  activeRuns,
  failedRuns,
  reports,
  totalReports,
  first,
  pageSize,
  onPage,
  onDismissFailed,
  onCancelRun,
  onDeleteReport,
  onDownload,
  busy,
}) => {
  const { t, i18n } = useTranslation();
  const periodLocale = periodLocaleFor(i18n.language);

  const periodBody = (from: string, to: string, label?: string) => {
    const display =
      label?.trim() || formatPeriodLabel(from, to, periodLocale);
    return <span title={`${from} – ${to}`}>{display}</span>;
  };
  const dateBody = (value: string) => format(new Date(value), 'yyyy-MM-dd / HH:mm');

  return (
    <div className="ai-visibility-report-list">
      {(activeRuns.length > 0 || failedRuns.length > 0) && (
        <>
          <div className="page-box__title-action">
            <div className="page-box__title page-box__title--flex">
              {t('aiVisibilityReport.runsTitle')}
            </div>
          </div>
          {activeRuns.length > 0 && (
            <div className="form__info ai-visibility-report-list__info">
              <svg
                className="ai-visibility-report-list__info-icon"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <circle cx="12" cy="12" r="10" />
                <path d="M12 16v-4" />
                <path d="M12 8h.01" />
              </svg>
              <div className="ai-visibility-report-list__info-body">
                <strong className="ai-visibility-report-list__info-title">
                  {t('aiVisibilityReport.generatingInfoTitle')}
                </strong>
                <span>{t('aiVisibilityReport.generatingInfo')}</span>
              </div>
            </div>
          )}
          <DataTable
            value={[...activeRuns, ...failedRuns]}
            dataKey="id"
            tableClassName="table"
            className="table-wrapper table-wrapper--stack"
            responsiveLayout="stack"
            emptyMessage={() => null}
          >
            <Column
              header={t('aiVisibilityReport.colPeriod')}
              headerClassName="table__header"
              bodyClassName="table__cell--stack-title"
              body={(row: AiVisibilityOverviewRun) => periodBody(row.periodFrom, row.periodTo)}
            />
            <Column
              header={t('aiVisibilityReport.colStatus')}
              headerClassName="table__header"
              body={(row: AiVisibilityOverviewRun) => (
                <MetadataBadge
                  variant={STATUS_VARIANT[row.status] ?? 'generating'}
                  text={t(`aiVisibilityReport.status.${row.status.toLowerCase()}`)}
                  pulse={row.status === 'GENERATING'}
                />
              )}
            />
            <Column
              header={t('aiVisibilityReport.colDate')}
              headerClassName="table__header"
              body={(row: AiVisibilityOverviewRun) => dateBody(row.createdAt)}
            />
            <Column
              header={t('aiVisibilityReport.colActions')}
              headerClassName="table__header"
              bodyClassName="table__cell--stack-wide"
              body={(row: AiVisibilityOverviewRun) => {
                if (row.status === 'FAILED') {
                  return (
                    <Button
                      className="btn btn--sm btn--secondary"
                      label={t('aiVisibilityReport.dismissBtn')}
                      disabled={busy}
                      onClick={() => onDismissFailed(row.id)}
                    />
                  );
                }
                if (row.status === 'GENERATING') {
                  return (
                    <Button
                      className="btn btn--sm btn--secondary"
                      label={t('aiVisibilityReport.cancelBtn')}
                      disabled={busy}
                      onClick={() => onCancelRun(row.id)}
                    />
                  );
                }
                return null;
              }}
            />
          </DataTable>
        </>
      )}

      <div className="page-box__title-action ai-visibility-report-list__reports-title">
        <div className="page-box__title page-box__title--flex">
          {t('aiVisibilityReport.reportsTitle')}
        </div>
      </div>
      <DataTable
        value={reports}
        dataKey="id"
        tableClassName="table"
        className="table-wrapper table-wrapper--stack"
        responsiveLayout="stack"
        emptyMessage={() => <EmptyPanel textKey="aiVisibilityReport.emptyState" />}
        paginator={totalReports > pageSize}
        lazy
        first={first}
        rows={pageSize}
        totalRecords={totalReports}
        onPage={onPage}
        rowsPerPageOptions={[10, 20, 50, 100]}
      >
        <Column
          header={t('aiVisibilityReport.colPeriod')}
          headerClassName="table__header"
          bodyClassName="table__cell--stack-title"
          body={(row: AiVisibilityOverviewReport) =>
            periodBody(row.periodFrom, row.periodTo, row.periodLabel)
          }
        />
        <Column
          header={t('aiVisibilityReport.colDate')}
          headerClassName="table__header"
          body={(row: AiVisibilityOverviewReport) => dateBody(row.createdAt)}
        />
        <Column
          header={t('aiVisibilityReport.colActions')}
          headerClassName="table__header"
          bodyClassName="table__cell--stack-wide"
          body={(row: AiVisibilityOverviewReport) => (
            <div className="ai-visibility-report-list__actions">
              <Button
                className="btn btn--sm"
                label={t('aiVisibilityReport.downloadBtn')}
                disabled={busy}
                onClick={() => onDownload(row.id, row.periodFrom, row.periodTo)}
              />
              <Button
                className="btn btn--sm btn--secondary"
                label={t('aiVisibilityReport.deleteBtn')}
                disabled={busy}
                onClick={() => onDeleteReport(row.id)}
              />
            </div>
          )}
        />
      </DataTable>
    </div>
  );
};

export default AiVisibilityReportList;
